import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * A horizontal rail that admits it scrolls.
 *
 * The rails here deliberately cut a card off at the edge — a half-visible card
 * is the cheapest way to say "there are more of these". Cut with a hard edge it
 * reads as a rendering bug instead, so the overflowing side fades into the card
 * behind it, and only the side that actually overflows.
 *
 * The fade earns a button: swiping a rail is natural on a phone and nobody's
 * instinct on a desktop with a mouse, and a vendor who can't see their fifth
 * venue shouldn't have to discover the gesture to reach it. The arrows appear
 * on exactly the sides that have somewhere to go.
 */

interface ScrollRailProps {
  /** On the scrolling element itself — gap, padding. */
  className?: string;
  /** On the wrapper the fades are positioned against — sizing, flex. */
  outerClassName?: string;
  /** Off for a rail whose own controls scroll it. */
  arrows?: boolean;
  children: React.ReactNode;
}

const ScrollRail = React.forwardRef<HTMLDivElement, ScrollRailProps>(
  ({ className, outerClassName, arrows = true, children }, ref) => {
    const innerRef = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(ref, () => innerRef.current as HTMLDivElement, []);

    const [edges, setEdges] = React.useState({ start: false, end: false });

    const measure = React.useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      // A couple of pixels of slack: sub-pixel widths otherwise leave a fade
      // stuck on at a rail that is already at its end.
      setEdges({ start: el.scrollLeft > 2, end: el.scrollLeft < max - 2 });
    }, []);

    React.useEffect(() => {
      const el = innerRef.current;
      if (!el) return;

      measure();
      el.addEventListener('scroll', measure, { passive: true });
      // Both the rail's own width and the number of cards in it can change
      // without a scroll — a venue switch reloads the courts underneath it.
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      Array.from(el.children).forEach((child) => observer.observe(child));

      return () => {
        el.removeEventListener('scroll', measure);
        observer.disconnect();
      };
    }, [measure, children]);

    // Not a fixed step: a rail of 62px date cards and one of 204px venue cards
    // both want "about a screenful", which is the only honest unit here.
    const scrollByPage = (direction: 1 | -1) => {
      const el = innerRef.current;
      if (!el) return;
      el.scrollBy({ left: direction * Math.max(el.clientWidth * 0.8, 160), behavior: 'smooth' });
    };

    const arrowClass =
      'absolute top-1/2 -translate-y-1/2 z-10 grid place-items-center w-7 h-7 rounded-full ' +
      'bg-flup-surface border border-flup-hairline-2 shadow-flup-card text-flup-ink-2 ' +
      'hover:bg-flup-surface-alt hover:text-flup-ink transition-colors ' +
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flup-brand';

    return (
      <div className={cn('relative min-w-0', outerClassName)}>
        <div ref={innerRef} className={cn('flup-rail', className)}>
          {children}
        </div>

        {edges.start ? (
          <>
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-flup-surface via-flup-surface to-transparent"
            />
            {arrows ? (
              <button
                type="button"
                aria-label="Scroll left"
                onClick={() => scrollByPage(-1)}
                className={cn(arrowClass, 'left-0')}
              >
                <ChevronLeft className="w-4 h-4" aria-hidden />
              </button>
            ) : null}
          </>
        ) : null}

        {edges.end ? (
          <>
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-flup-surface via-flup-surface to-transparent"
            />
            {arrows ? (
              <button
                type="button"
                aria-label="Scroll right"
                onClick={() => scrollByPage(1)}
                className={cn(arrowClass, 'right-0')}
              >
                <ChevronRight className="w-4 h-4" aria-hidden />
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    );
  },
);

ScrollRail.displayName = 'ScrollRail';

export default ScrollRail;
